/* =========================================================================
   Agents.js — Deep Agents tab (replaces Assignments.js)
   ========================================================================= */
import React, { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../../utils/authFetch';
import {
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableExpandHeader,
  TableExpandRow,
  TableExpandedRow,
  TableCell,
  Loading,
  Tooltip,
  Button,
  Tag,
  InlineLoading,
  Modal,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
} from '@carbon/react';
import {
  ArrowRight,
  Demo,
  Information,
  Renew,
  TrashCan,
  PlayOutline,
  DocumentView,
  CheckmarkOutline,
  Activity,
  Settings,
} from '@carbon/react/icons';
import ReactMarkdown from 'react-markdown';
import JEmoji from 'emoji-toolkit';
import { v4 as uuidv4 } from 'uuid';
import { ModelSelector } from './ModelSelector';
import { modelInfo } from './ModelInfo';
import SpeechInput from '../../components/SpeechInput/SpeechInput';
import { VolumeUp, VolumeMute } from '@carbon/react/icons';
import { speak, stopEx } from '../../utils/speechUtils';

/* ---------- helpers ---------- */
const formatLocalDateTime = ts => {
  if (!ts) return '\u2014';
  // Only append Z if no timezone offset is already present (handles both legacy
  // space-separated UTC strings and modern ISO strings with +/-HH:MM offsets)
  const normalized = ts.replace(' ', 'T');
  const iso = /[+-]\d{2}:\d{2}$|Z$/.test(normalized)
    ? normalized
    : `${normalized}Z`;
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '\u2014'
    : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

const truncate = (s, n) => (s && s.length > n ? s.slice(0, n) + '…' : s || '');

/* ========================================================================
   TOP-LEVEL COMPONENT
======================================================================== */
export default function Agents({ token, user }) {
  /* chat / ai state */
  const [conversationId, setConversationId] = useState(null);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [input, setInput] = useState('');
  const [responseText, setResponseText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [voiceMode, setVoiceMode] = useState(false);
  useEffect(() => {
    return () => stopEx();
  }, []);

  /* model selector */
  const [selectedModel, setSelectedModel] = useState(
    () => localStorage.getItem('Agents_selectedModel') || 'claude-sonnet-4-6'
  );
  const [isModelModalOpen, setIsModelModalOpen] = useState(false);
  const TECH_KEY = 'Agents_showTechnicalSpecs';

  /* data for tables */
  const [agents, setAgents] = useState([]);
  const [periodicRows, setPeriodicRows] = useState([]);

  /* modal */
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState('');
  const [modalHeading, setModalHeading] = useState('');

  const placeholderText =
    'Describe a task for an autonomous agent, or manage periodic tasks...';
  const formattedResponse = JEmoji.shortnameToUnicode(responseText);

  /* conversation id management */
  const saveCurrentConversationId = useCallback(
    async id => {
      if (!token) return;
      await authFetch('/mlapi/premium/task/data/save/agents/current_id', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ value: id, ttl: 0 }),
      });
    },
    [token]
  );

  useEffect(() => {
    if (!token) return;
    (async () => {
      const r = await authFetch(
        'mlapi/premium/task/data/get/agents/current_id',
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const txt = r.ok ? await r.text() : '';
      let id = txt.replace(/^"|"$/g, '');
      if (!id) {
        id = uuidv4();
        await saveCurrentConversationId(id);
      }
      setConversationId(id);
    })();
  }, [token, saveCurrentConversationId]);

  /* fetch helpers */
  const fetchAgents = useCallback(async () => {
    if (!token) return;
    try {
      const r = await authFetch('/mlapi/premium/agent/list', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setAgents(await r.json());
    } catch (e) {
      console.error('fetchAgents error:', e);
    }
  }, [token]);

  const fetchPeriodicRows = useCallback(async () => {
    if (!token) return;
    try {
      const r = await authFetch(
        'mlapi/premium/task/state/table_list/periodic',
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (r.ok) setPeriodicRows(await r.json());
    } catch (e) {
      console.error('fetchPeriodicRows error:', e);
    }
  }, [token]);

  /* WebSocket for real-time agent updates */
  useEffect(() => {
    if (!token) return;
    fetchAgents();
    fetchPeriodicRows();

    const { protocol, host } = window.location;
    const wsUrl = `${
      protocol === 'https:' ? 'wss:' : 'ws:'
    }//${host}/mlapi/premium/agent/ws/updates/${user}`;
    let ws;
    try {
      ws = new WebSocket(wsUrl);
      ws.onmessage = () => {
        fetchAgents();
      };
      ws.onerror = e => console.debug('Agent WS error:', e);
    } catch (e) {
      console.debug('Agent WS connect error:', e);
    }

    // Also listen on old assignment WS for periodic task updates
    const wsUrl2 = `${
      protocol === 'https:' ? 'wss:' : 'ws:'
    }//${host}/mlapi/premium/assignment/ws/assignment_updates/${user}`;
    let ws2;
    try {
      ws2 = new WebSocket(wsUrl2);
      ws2.onmessage = () => {
        fetchPeriodicRows();
      };
    } catch (e) {
      /* ignore */
    }

    return () => {
      if (ws) ws.close();
      if (ws2) ws2.close();
    };
  }, [token, user, fetchAgents, fetchPeriodicRows]);

  useEffect(() => {
    localStorage.setItem('Agents_selectedModel', selectedModel);
  }, [selectedModel]);

  /* conversation update */
  const updateConversation = async latest => {
    if (!token || !conversationId) return;
    await authFetch(
      `/mlapi/basic/conversation/${encodeURIComponent(conversationId)}/update`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          messages: latest,
          content_type: 'ai_agent',
          model_id: selectedModel,
        }),
      }
    );
  };

  /* submit chat */
  const handleSubmit = async (overrideInput = null) => {
    const textToUse = typeof overrideInput === 'string' ? overrideInput : input;
    if (!token || !conversationId || !textToUse.trim()) return;
    setIsLoading(true);
    const now = new Date().toISOString();

    let finalInput = textToUse;
    if (voiceMode) {
      finalInput +=
        ' (SYSTEM: User is in eyes-free voice mode. Keep response short, conversational, plain text only. No markdown tables/code.)';
    }

    const messages = [
      ...conversationHistory.flatMap(h => [
        { role: 'user', content: h.question },
        { role: 'ai', content: h.answer },
      ]),
      {
        role: 'user',
        content: `(Please respond in Markdown format. The date and time is currently: ${now}) My question: ${finalInput}`,
      },
    ];
    setResponseText(`\n\n**${textToUse}**\n\n`);
    if (typeof overrideInput !== 'string') setInput('');

    try {
      const resp = await authFetch(
        `/mlapi/basic/stream-llm-tools?model=${selectedModel}&tools=create_agent`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(messages),
        }
      );
      const reader = resp.body.getReader();
      const dec = new TextDecoder('utf-8');
      let acc = '';
      let first = true;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = dec.decode(value, { stream: true }).replace('[DONE]', '');
        if (first) {
          setIsLoading(false);
          first = false;
        }
        acc += chunk;
        setResponseText(p => p + chunk);
      }
      setConversationHistory(h => [...h, { question: textToUse, answer: acc }]);
      await updateConversation([
        { role: 'user', content: textToUse },
        { role: 'system', content: acc },
      ]);
      if (voiceMode) speak(acc);
      fetchAgents();
      fetchPeriodicRows();
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  /* modal helpers */
  const showModal = (heading, content) => {
    setModalHeading(heading);
    setModalContent(
      typeof content === 'object' ? JSON.stringify(content, null, 2) : content
    );
    setIsModalOpen(true);
  };

  /* ---- RENDER ---- */
  return (
    <>
      {/* AI Chat Section */}
      <div className="accordion-top-space">
        Create and manage autonomous AI agents that can plan, research, and
        execute complex tasks. Agents use tools like web search, email, file
        storage, and more. &nbsp;&nbsp;
        <Tooltip
          label="Watch a tutorial video about Tasks."
          align="bottom"
          style={{ marginLeft: '8px' }}>
          <Button
            kind="ghost"
            size="sm"
            onClick={() =>
              window.open('https://youtu.be/u9EVu5dhhcQ', '_blank', 'noopener')
            }
            className="tooltip-button"
            aria-label="Watch tutorial video">
            <Demo />
          </Button>
        </Tooltip>
      </div>
      <div className="model-selector-container">
        <ModelSelector
          selectedModel={selectedModel}
          setSelectedModel={setSelectedModel}
          isModelModalOpen={isModelModalOpen}
          setIsModelModalOpen={setIsModelModalOpen}
          techSpecsStorageKey={TECH_KEY}
          legacyStorageKey="Agents_showLegacyModels"
        />
        <Tooltip label="Model selection help" align="bottom">
          <Button kind="ghost" size="sm">
            <Information />
          </Button>
        </Tooltip>
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: '0.5rem',
          marginBottom: '0.5rem',
        }}>
        <div style={{ flex: 1 }}>
          <textarea
            className="llm-request-textarea"
            placeholder={placeholderText}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            style={{ marginBottom: 0 }}
          />
        </div>
        <SpeechInput
          onTranscript={text =>
            setInput(prev => prev + (prev ? ' ' : '') + text)
          }
          onSubmit={text => {
            const fullText = input + (input ? ' ' : '') + text;
            setInput(fullText);
            handleSubmit(fullText);
            setInput('');
          }}
        />
        <Tooltip
          label={voiceMode ? 'Voice Mode ON' : 'Enable Voice Mode'}
          align="bottom-right">
          <Button
            hasIconOnly
            renderIcon={voiceMode ? VolumeUp : VolumeMute}
            iconDescription={
              voiceMode ? 'Disable Voice Mode' : 'Enable Voice Mode'
            }
            kind="ghost"
            size="sm"
            onClick={() => {
              const n = !voiceMode;
              setVoiceMode(n);
              if (!n) stopEx();
            }}
            style={{ color: voiceMode ? '#0f62fe' : undefined }}
          />
        </Tooltip>
      </div>
      <Button
        kind="primary"
        size="sm"
        disabled={isLoading}
        onClick={handleSubmit}>
        Discuss <ArrowRight />
      </Button>{' '}
      <Button
        kind="secondary"
        size="sm"
        onClick={() => {
          setConversationHistory([]);
          setResponseText('');
        }}>
        Clear
      </Button>
      <div className="llm-response-area">
        <ReactMarkdown>{formattedResponse}</ReactMarkdown>
        {isLoading && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              padding: '1rem',
            }}>
            <Loading
              small
              description="Generating response"
              withOverlay={false}
            />
            <span className="cds--label">Generating response…</span>
          </div>
        )}
      </div>
      {/* Agents Table */}
      <AgentsTable
        rows={agents}
        token={token}
        refresh={fetchAgents}
        showModal={showModal}
      />
      {/* Periodic Tasks Table (reused from Assignments) */}
      <PeriodicTasksTable
        periodicRows={periodicRows}
        token={token}
        refresh={fetchPeriodicRows}
      />
      {isModalOpen && (
        <Modal
          open
          modalHeading={modalHeading}
          primaryButtonText="Close"
          onRequestClose={() => setIsModalOpen(false)}
          passiveModal
          size="lg">
          <div style={{ margin: 20, maxHeight: '60vh', overflow: 'auto' }}>
            <ReactMarkdown>{modalContent}</ReactMarkdown>
          </div>
        </Modal>
      )}
    </>
  );
}

/* =====================================================================
   Inline Model Dropdown (for use inside table cells)
===================================================================== */
function ModelInlineDropdown({ currentModel, onModelChange, size = 'sm' }) {
  const models = Object.keys(modelInfo).filter(id => {
    const m = modelInfo[id];
    // Skip thinking-only variants that can't use tools
    return m.parameters?.toolUse !== false;
  });

  return (
    <select
      value={currentModel || ''}
      onChange={e => onModelChange(e.target.value)}
      className="model-inline-select"
      style={{
        maxWidth: '160px',
        fontSize: '0.8rem',
        padding: '2px 4px',
        border: '1px solid #e0e0e0',
        borderRadius: '4px',
        background: '#fff',
        cursor: 'pointer',
      }}>
      {models.map(mId => (
        <option key={mId} value={mId}>
          {modelInfo[mId]?.name || mId}
        </option>
      ))}
    </select>
  );
}

/* =====================================================================
   Agents Table
===================================================================== */
function AgentsTable({ rows, token, refresh, showModal }) {
  const headers = [
    { key: 'name', header: 'Name' },
    { key: 'description', header: 'Description' },
    { key: 'model', header: 'Model' },
    { key: 'last_completed_datetime', header: 'Last Run' },
    { key: 'number_of_times_completed', header: 'Runs' },
    { key: 'actions', header: 'Actions' },
  ];

  const tableRows = (rows || []).map(a => ({
    id: a.id,
    name: a.name || '',
    description: truncate(a.description, 80) || '',
    model: a.model || '',
    last_completed_datetime: formatLocalDateTime(a.last_completed_datetime),
    number_of_times_completed: a.number_of_times_completed || 0,
    actions: a.id,
    // Keep full data for expanded view
    _full: a,
  }));

  return (
    <DataTable rows={tableRows} headers={headers} isSortable>
      {({
        rows: tRows,
        headers: tHeaders,
        getHeaderProps,
        getRowProps,
        getTableProps,
        getTableContainerProps,
        onInputChange,
      }) => (
        <TableContainer
          description="Autonomous AI agents"
          title="Agents"
          {...getTableContainerProps()}>
          <TableToolbar>
            <TableToolbarContent>
              <TableToolbarSearch onChange={onInputChange} />
              <Button
                hasIconOnly
                renderIcon={Renew}
                iconDescription="Refresh"
                onClick={refresh}
              />
            </TableToolbarContent>
          </TableToolbar>

          <Table {...getTableProps()}>
            <TableHead>
              <TableRow>
                <TableExpandHeader />
                {tHeaders.map(h => (
                  <TableHeader key={h.key} {...getHeaderProps({ header: h })}>
                    {h.header}
                  </TableHeader>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tRows.map(r => {
                const fullAgent = tableRows.find(tr => tr.id === r.id)?._full;
                return (
                  <AgentRow
                    key={r.id}
                    rowData={r}
                    fullAgent={fullAgent}
                    token={token}
                    refreshList={refresh}
                    showModal={showModal}
                    {...getRowProps({ row: r })}
                  />
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </DataTable>
  );
}

/* =====================================================================
   Agent Row (expandable)
===================================================================== */
function AgentRow({
  rowData,
  fullAgent,
  token,
  refreshList,
  showModal,
  ...rowProps
}) {
  const [expanded, setExpanded] = useState(false);
  const [runs, setRuns] = useState([]);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [runningId, setRunningId] = useState(null);
  const [promptOpen, setPromptOpen] = useState(false);
  const [promptContent, setPromptContent] = useState('');

  const { id } = rowData;
  const name = rowData.cells.find(c => c.info.header === 'name')?.value || '';

  /* model change handler */
  const handleModelChange = useCallback(
    async newModel => {
      if (!token) return;
      try {
        const r = await authFetch(`/mlapi/premium/agent/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ model: newModel }),
        });
        if (r.ok) refreshList();
      } catch (e) {
        console.error('Failed to update agent model:', e);
      }
    },
    [id, token, refreshList]
  );

  const fetchRuns = useCallback(async () => {
    if (!token) return;
    setLoadingRuns(true);
    try {
      const r = await authFetch(`/mlapi/premium/agent/${id}/runs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setRuns(await r.json());
    } catch (e) {
      console.error(e);
    }
    setLoadingRuns(false);
  }, [id, token]);

  const toggle = () => {
    if (!expanded) fetchRuns();
    setExpanded(e => !e);
  };

  const runAgent = async e => {
    e.stopPropagation();
    if (!token) return;
    setRunningId(id);
    try {
      const r = await authFetch(`/mlapi/premium/agent/${id}/run`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ input_message: '' }),
      });
      if (r.ok) {
        const data = await r.json();
        console.log('Agent started, run_id:', data.run_id);
      }
    } catch (e) {
      console.error(e);
    }
    setTimeout(() => {
      setRunningId(null);
      refreshList();
    }, 2000);
  };

  const deleteAgent = async e => {
    e.stopPropagation();
    if (!token || !window.confirm(`Delete agent "${name}"?`)) return;
    await authFetch(`/mlapi/premium/agent/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    refreshList();
  };

  const viewConfig = e => {
    e.stopPropagation();
    if (fullAgent) {
      showModal(`Agent: ${name}`, JSON.stringify(fullAgent, null, 2));
    }
  };

  const openPrompt = async e => {
    e.stopPropagation();
    try {
      const r = await authFetch(`/mlapi/premium/agent/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const config = r.ok ? await r.json() : null;
      setPromptContent(config?.system_prompt || '');
    } catch (err) {
      console.error('Failed to load agent prompt:', err);
      setPromptContent('');
    }
    setPromptOpen(true);
  };

  const savePrompt = async newContent => {
    await authFetch(`/mlapi/premium/agent/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ system_prompt: newContent }),
    });
    refreshList();
  };

  return (
    <>
      <TableExpandRow {...rowProps} isExpanded={expanded} onExpand={toggle}>
        {rowData.cells.map(cell => {
          if (cell.info.header === 'actions') {
            return (
              <TableCell key={cell.id}>
                <Tooltip label="Run">
                  <Button
                    size="sm"
                    kind="ghost"
                    renderIcon={PlayOutline}
                    iconDescription="Run"
                    onClick={runAgent}
                    disabled={runningId === id}
                  />
                </Tooltip>
                <Tooltip label="Config">
                  <Button
                    size="sm"
                    kind="ghost"
                    renderIcon={Settings}
                    iconDescription="View Config"
                    onClick={viewConfig}
                  />
                </Tooltip>
                <Tooltip label="View / Edit Prompt">
                  <Button
                    size="sm"
                    kind="ghost"
                    renderIcon={DocumentView}
                    iconDescription="Prompt"
                    onClick={openPrompt}
                  />
                </Tooltip>
                <Tooltip label="Delete">
                  <Button
                    size="sm"
                    kind="ghost"
                    renderIcon={TrashCan}
                    iconDescription="Delete"
                    onClick={deleteAgent}
                  />
                </Tooltip>
              </TableCell>
            );
          }
          // Model column — render inline dropdown
          if (cell.info.header === 'model') {
            return (
              <TableCell key={cell.id}>
                <ModelInlineDropdown
                  currentModel={cell.value}
                  onModelChange={handleModelChange}
                />
              </TableCell>
            );
          }
          return <TableCell key={cell.id}>{cell.value}</TableCell>;
        })}
      </TableExpandRow>

      {expanded && (
        <TableExpandedRow colSpan={headers_count}>
          {loadingRuns && <InlineLoading description="Loading runs…" />}
          {!loadingRuns && (
            <AgentRunHistory
              runs={runs}
              token={token}
              agentId={id}
              showModal={showModal}
              onRefresh={fetchRuns}
            />
          )}
        </TableExpandedRow>
      )}
      <PromptEditModal
        open={promptOpen}
        title={`Prompt — ${name}`}
        initialContent={promptContent}
        onSave={savePrompt}
        onClose={() => setPromptOpen(false)}
      />
    </>
  );
}

const headers_count = 8; // 6 headers + expand + actions

/* =====================================================================
   Agent Run History
===================================================================== */
function AgentRunHistory({ runs, token, agentId, showModal, onRefresh }) {
  const viewRunDetails = async runId => {
    if (!token) return;
    try {
      const r = await authFetch(
        `/mlapi/premium/agent/${agentId}/run/${runId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (r.ok) {
        const data = await r.json();
        showModal(`Run ${runId.slice(0, 8)}...`, JSON.stringify(data, null, 2));
      }
    } catch (e) {
      console.error(e);
    }
  };

  const viewRunEvents = async runId => {
    if (!token) return;
    try {
      const r = await authFetch(
        `/mlapi/premium/agent/${agentId}/run/${runId}/events`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (r.ok) {
        const events = await r.json();
        const formatted = events
          .map(e => {
            if (e.type === 'tool_call')
              return `**Tool:** ${e.tool} \u2192 ${e.status}`;
            if (e.type === 'tool_result')
              return `**Result:** ${e.tool}: ${truncate(e.summary, 200)}`;
            return `**${e.type}**: ${JSON.stringify(e)}`;
          })
          .join('\n\n');
        showModal(
          `Events for run ${runId.slice(0, 8)}...`,
          formatted || 'No events recorded.'
        );
      }
    } catch (e) {
      console.error(e);
    }
  };

  if (!runs || runs.length === 0) {
    return (
      <div style={{ padding: '1rem', color: '#666' }}>
        No runs yet. Click "Run" to execute this agent.
      </div>
    );
  }

  return (
    <div style={{ padding: '0.5rem' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: '0.5rem',
        }}>
        <strong>Run History</strong>
        <Button
          hasIconOnly
          size="sm"
          kind="ghost"
          renderIcon={Renew}
          iconDescription="Refresh"
          onClick={onRefresh}
        />
      </div>
      <table
        style={{
          width: '100%',
          fontSize: '0.85rem',
          borderCollapse: 'collapse',
        }}>
        <thead>
          <tr style={{ borderBottom: '1px solid #e0e0e0', textAlign: 'left' }}>
            <th style={{ padding: '4px 8px' }}>Status</th>
            <th style={{ padding: '4px 8px' }}>Started</th>
            <th style={{ padding: '4px 8px' }}>Completed</th>
            <th style={{ padding: '4px 8px' }}>Summary</th>
            <th style={{ padding: '4px 8px' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {runs.slice(0, 10).map(run => (
            <tr key={run.run_id} style={{ borderBottom: '1px solid #f0f0f0' }}>
              <td style={{ padding: '4px 8px' }}>
                <Tag
                  type={
                    run.status === 'completed'
                      ? 'green'
                      : run.status === 'failed'
                      ? 'red'
                      : 'blue'
                  }
                  size="sm">
                  {run.status}
                </Tag>
              </td>
              <td style={{ padding: '4px 8px' }}>
                {formatLocalDateTime(run.started_at)}
              </td>
              <td style={{ padding: '4px 8px' }}>
                {formatLocalDateTime(run.completed_at)}
              </td>
              <td style={{ padding: '4px 8px' }}>
                {truncate(run.summary || run.error || '', 100)}
              </td>
              <td style={{ padding: '4px 8px' }}>
                <Tooltip label="View Details">
                  <Button
                    size="sm"
                    kind="ghost"
                    renderIcon={DocumentView}
                    iconDescription="Details"
                    onClick={() => viewRunDetails(run.run_id)}
                  />
                </Tooltip>
                <Tooltip label="View Events">
                  <Button
                    size="sm"
                    kind="ghost"
                    renderIcon={Activity}
                    iconDescription="Events"
                    onClick={() => viewRunEvents(run.run_id)}
                  />
                </Tooltip>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* =====================================================================
   Periodic Tasks Table (same as Assignments.js)
===================================================================== */
function PeriodicTasksTable({ periodicRows, token, refresh }) {
  const [promptModal, setPromptModal] = useState({
    open: false,
    title: '',
    content: '',
    taskId: null,
    agentId: null,
  });

  const openPrompt = async rowId => {
    const p = (periodicRows || []).find(r => r.id === rowId);
    if (!p) return;
    if (p.type === 'deep_agent' && p.agent_id) {
      const r = await authFetch(`/mlapi/premium/agent/${p.agent_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const agentCfg = r.ok ? await r.json() : null;
      setPromptModal({
        open: true,
        title: `${p.name} — System Prompt (via agent: ${p.agent_name ||
          p.agent_id})`,
        content: agentCfg?.system_prompt || '',
        taskId: null,
        agentId: p.agent_id,
      });
    } else if (p.type === 'ai_with_tools') {
      setPromptModal({
        open: true,
        title: `${p.name} — AI Prompt`,
        content: p.ai_prompt || '',
        taskId: p.id,
        agentId: null,
      });
    }
  };

  const savePrompt = async newContent => {
    const { taskId, agentId } = promptModal;
    if (agentId) {
      await authFetch(`/mlapi/premium/agent/${agentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ system_prompt: newContent }),
      });
    } else if (taskId) {
      await authFetch(
        `/mlapi/premium/task/state/update_prompt/periodic/${taskId}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ ai_prompt: newContent }),
        }
      );
    }
    setPromptModal({ open: false });
    refresh();
  };

  const headers = [
    { key: 'name', header: 'Name' },
    { key: 'description', header: 'Description' },
    { key: 'schedule', header: 'Schedule' },
    { key: 'enabled', header: 'Enabled' },
    { key: 'model', header: 'Active Model' },
    { key: 'last_run_time', header: 'Last Run' },
    { key: 'runs_as', header: 'Runs As' },
    { key: 'id', header: 'ID' },
    { key: 'actions', header: 'Actions' },
  ];

  const toggleEnabled = async id => {
    if (!token) return;
    await authFetch(`mlapi/premium/task/state/toggle_enabled/periodic/${id}`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token}` },
    });
    refresh();
  };
  const del = async id => {
    if (!token || !window.confirm('Delete periodic task?')) return;
    await authFetch(`mlapi/premium/task/state/delete/periodic/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    refresh();
  };

  const rows = (periodicRows || []).map(p => {
    let runsAs = '';
    const taskModel = p.model || 'gpt-4.1-mini';
    const agentModel = p.agent_model || '';

    if (p.type === 'deep_agent' && p.agent_name) {
      runsAs = `Agent: ${p.agent_name}`;
      if (agentModel && agentModel !== taskModel) {
        runsAs += ` (agent default: ${agentModel})`;
      }
    } else if (p.type === 'deep_agent' && p.agent_id) {
      runsAs = `Agent: ${p.agent_id.slice(0, 8)}…`;
    } else if (p.type === 'ai_with_tools') {
      const tools = Array.isArray(p.tools) ? p.tools : [];
      runsAs = tools.length > 0 ? `AI + ${tools.length} tools` : 'AI prompt';
    } else if (p.type === 'assignment_task' || p.type === 'agent_task') {
      runsAs = 'Agent (sequential)';
    } else {
      runsAs = p.type || '\u2014';
    }
    return {
      name: p.name,
      description: p.description,
      schedule: p.schedule,
      enabled: p.enabled ? 'Yes' : 'No',
      last_run_time: formatLocalDateTime(p.last_run_time),
      model: taskModel,
      runs_as: runsAs,
      id: p.id,
      actions: p.id,
    };
  });

  return (
    <>
      <DataTable rows={rows} headers={headers} isSortable size="sm">
        {({
          rows: tRows,
          headers: tHeaders,
          getHeaderProps,
          getRowProps,
          getTableProps,
          getTableContainerProps,
          onInputChange,
        }) => (
          <TableContainer
            title="Scheduled Tasks"
            description="AI jobs that run on a schedule. For Agent tasks, the Active Model column shows the model that will actually run — it overrides the agent's own configured model. The 'Runs As' column shows the agent name and its default model (if different)."
            style={{ marginTop: '2rem' }}
            {...getTableContainerProps()}>
            <TableToolbar>
              <TableToolbarContent>
                <TableToolbarSearch onChange={onInputChange} />
                <Button
                  hasIconOnly
                  renderIcon={Renew}
                  iconDescription="Refresh"
                  onClick={refresh}
                />
              </TableToolbarContent>
            </TableToolbar>

            <Table {...getTableProps()}>
              <TableHead>
                <TableRow>
                  {tHeaders.map(h => (
                    <TableHeader key={h.key} {...getHeaderProps({ header: h })}>
                      {h.header}
                    </TableHeader>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {tRows.map(row => (
                  <TableRow key={row.id} {...getRowProps({ row })}>
                    {row.cells.map(cell => {
                      if (cell.info.header === 'actions') {
                        return (
                          <TableCell key={cell.id}>
                            <Tooltip label="Enable / Disable">
                              <Button
                                size="sm"
                                kind="ghost"
                                renderIcon={CheckmarkOutline}
                                onClick={() => toggleEnabled(row.id)}
                              />
                            </Tooltip>
                            <Tooltip label="View / Edit Prompt">
                              <Button
                                size="sm"
                                kind="ghost"
                                renderIcon={DocumentView}
                                iconDescription="Prompt"
                                onClick={() => openPrompt(row.id)}
                              />
                            </Tooltip>
                            <Tooltip label="Delete">
                              <Button
                                size="sm"
                                kind="ghost"
                                renderIcon={TrashCan}
                                onClick={() => del(row.id)}
                              />
                            </Tooltip>
                          </TableCell>
                        );
                      }
                      // Model column — render inline dropdown
                      if (cell.info.header === 'model') {
                        return (
                          <TableCell key={cell.id}>
                            <ModelInlineDropdown
                              currentModel={cell.value || ''}
                              onModelChange={async newModel => {
                                if (!token || !row.id) return;
                                try {
                                  await authFetch(
                                    `/mlapi/premium/task/state/update_model/periodic/${encodeURIComponent(
                                      row.id
                                    )}`,
                                    {
                                      method: 'PATCH',
                                      headers: {
                                        'Content-Type': 'application/json',
                                        Authorization: `Bearer ${token}`,
                                      },
                                      body: JSON.stringify({ model: newModel }),
                                    }
                                  );
                                  refresh();
                                } catch (e) {
                                  console.error(
                                    'Failed to update periodic task model:',
                                    e
                                  );
                                }
                              }}
                            />
                          </TableCell>
                        );
                      }
                      return <TableCell key={cell.id}>{cell.value}</TableCell>;
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DataTable>
      <PromptEditModal
        open={promptModal.open}
        title={promptModal.title}
        initialContent={promptModal.content}
        onSave={savePrompt}
        onClose={() => setPromptModal({ open: false })}
      />
    </>
  );
}

/* =====================================================================
   Prompt View / Edit Modal
   Shared by AgentsTable rows and PeriodicTasksTable rows.
===================================================================== */
function PromptEditModal({ open, title, initialContent, onSave, onClose }) {
  const [isEditing, setIsEditing] = useState(false);
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setContent(initialContent || '');
      setIsEditing(false);
      setSaving(false);
    }
  }, [open, initialContent]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(content);
    } finally {
      setSaving(false);
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setContent(initialContent || '');
    setIsEditing(false);
  };

  return (
    <Modal
      open={open}
      modalHeading={title}
      size="lg"
      primaryButtonText={isEditing ? (saving ? 'Saving…' : 'Save') : 'Close'}
      secondaryButtonText={isEditing ? 'Cancel' : 'Edit'}
      primaryButtonDisabled={saving}
      onRequestSubmit={isEditing ? handleSave : onClose}
      onSecondarySubmit={isEditing ? handleCancel : () => setIsEditing(true)}
      onRequestClose={onClose}
      preventCloseOnClickOutside={isEditing}>
      <div style={{ padding: '0.5rem 0 1rem' }}>
        {isEditing ? (
          <textarea
            style={{
              width: '100%',
              minHeight: '60vh',
              fontFamily: 'monospace',
              fontSize: '13px',
              lineHeight: '1.5',
              padding: '0.75rem',
              resize: 'vertical',
              border: '1px solid #8d8d8d',
              borderRadius: '4px',
              boxSizing: 'border-box',
            }}
            value={content}
            onChange={e => setContent(e.target.value)}
            autoFocus
          />
        ) : (
          <div
            style={{
              maxHeight: '65vh',
              overflow: 'auto',
              background: '#f4f4f4',
              padding: '1rem',
              borderRadius: '4px',
            }}>
            {content ? (
              <ReactMarkdown>{content}</ReactMarkdown>
            ) : (
              <span style={{ color: '#6f6f6f', fontStyle: 'italic' }}>
                (no prompt configured)
              </span>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
